import React, { useRef, useState } from "react";
import { apiClient } from "../../../lib/api";
import { useI18n } from "../../../lib/i18n";
import { toast } from "sonner";
import { Upload } from "lucide-react";

export default function DashProfile({ businessId, home }) {
  const { lang } = useI18n();
  const biz = home?.business || {};
  const [form, setForm] = useState({
    name_ar: biz.name_ar || "",
    name_en: biz.name_en || "",
    profile: {
      bio_ar: biz.profile?.bio_ar || "",
      bio_en: biz.profile?.bio_en || "",
      phone: biz.profile?.phone || "",
      email: biz.profile?.email || "",
      website: biz.profile?.website || "",
      city: biz.profile?.city || "",
      address_ar: biz.profile?.address_ar || "",
      logo_url: biz.profile?.logo_url || "",
      cover_url: biz.profile?.cover_url || "",
    },
  });
  const [saving, setSaving] = useState(false);
  // iter295 — track which URL field is currently uploading so we can
  // disable that button and show a small spinner. Native file picker
  // only; no in-app media library, no ChatGPT gallery.
  const [uploadingField, setUploadingField] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.patch(`/platform/businesses/${businessId}`, form);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const upd = (path, val) => {
    if (path.includes(".")) {
      const [a, b] = path.split(".");
      setForm({ ...form, [a]: { ...form[a], [b]: val } });
    } else {
      setForm({ ...form, [path]: val });
    }
  };

  // iter295 — Upload a picked image to the existing /api/upload service
  // and place the resulting absolute URL into the given form path
  // (either `profile.logo_url` or `profile.cover_url`). Uses `kind=business`
  // so the vehicle watermark pipeline is never triggered. If the user
  // types a URL manually the field keeps working exactly as before.
  const uploadImage = async (fieldPath, file) => {
    if (!file) return;
    if (!(file.type || "").startsWith("image/")) {
      toast.error(lang === "ar" ? "الرجاء اختيار ملف صورة." : "Please choose an image file.");
      return;
    }
    setUploadingField(fieldPath);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/upload?kind=business", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const abs = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      upd(fieldPath, abs);
      toast.success(lang === "ar" ? "تم رفع الصورة" : "Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (lang === "ar" ? "فشل الرفع" : "Upload failed"));
    } finally {
      setUploadingField(null);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="dash-profile">
      {/* Cover + Logo preview */}
      <div className="rounded-2xl overflow-hidden bg-white border border-[#0a0a0a]/[0.06]">
        <div className="h-32 sm:h-40 relative" style={{ background: form.profile.cover_url ? `url(${form.profile.cover_url}) center/cover` : "linear-gradient(135deg,#0a0a0a,#1f1614)" }}>
          <div className="absolute -bottom-8 start-6 w-20 h-20 rounded-2xl border-4 border-white overflow-hidden shadow-lg" style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)" }}>
            {form.profile.logo_url ? <img src={form.profile.logo_url} alt="" className="w-full h-full object-cover" /> : (
              <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-[#0a0a0a]">{(form.name_ar || "?").charAt(0)}</div>
            )}
          </div>
        </div>
        <div className="pt-10 pb-5 px-6">
          <div className="text-lg font-bold">{form.name_ar || biz.name_ar}</div>
          <div className="text-[12px] text-[#8a8a8a]">{biz.category}</div>
        </div>
      </div>

      <Grid>
        <Field labelAr="اسم النشاط (عربي)" labelEn="Name (Arabic)" required
               value={form.name_ar} onChange={(v) => upd("name_ar", v)} />
        <Field labelAr="اسم النشاط (إنجليزي)" labelEn="Name (English)"
               value={form.name_en} onChange={(v) => upd("name_en", v)} />
        <UploadField labelAr="رابط الشعار (URL)" labelEn="Logo URL"
                     btnAr="تحميل الشعار" btnEn="Upload logo"
                     value={form.profile.logo_url}
                     onChange={(v) => upd("profile.logo_url", v)}
                     onPick={(f) => uploadImage("profile.logo_url", f)}
                     busy={uploadingField === "profile.logo_url"}
                     testid="upload-logo" />
        <UploadField labelAr="رابط الغلاف (URL)" labelEn="Cover URL"
                     btnAr="تحميل الغلاف" btnEn="Upload cover"
                     value={form.profile.cover_url}
                     onChange={(v) => upd("profile.cover_url", v)}
                     onPick={(f) => uploadImage("profile.cover_url", f)}
                     busy={uploadingField === "profile.cover_url"}
                     testid="upload-cover" />
        <Field labelAr="الهاتف" labelEn="Phone" ltr
               value={form.profile.phone} onChange={(v) => upd("profile.phone", v)} />
        <Field labelAr="البريد الإلكتروني" labelEn="Email" ltr
               value={form.profile.email} onChange={(v) => upd("profile.email", v)} />
        <Field labelAr="الموقع الإلكتروني" labelEn="Website" ltr
               value={form.profile.website} onChange={(v) => upd("profile.website", v)} />
        <Field labelAr="المدينة" labelEn="City"
               value={form.profile.city} onChange={(v) => upd("profile.city", v)} />
      </Grid>

      <TextArea labelAr="نبذة (عربي)" labelEn="Bio (Arabic)"
                value={form.profile.bio_ar} onChange={(v) => upd("profile.bio_ar", v)} />
      <TextArea labelAr="Bio (English)" labelEn="Bio (English)"
                value={form.profile.bio_en} onChange={(v) => upd("profile.bio_en", v)} />

      <div className="flex justify-end">
        <button type="submit" disabled={saving}
                className="px-6 py-2.5 rounded-full text-sm font-bold shadow-sm disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#e8c873,#c9a55c)", color: "#0a0a0a" }}
                data-testid="btn-save-profile">
          {saving ? "…" : (lang === "ar" ? "حفظ التغييرات" : "Save changes")}
        </button>
      </div>
    </form>
  );
}

function Grid({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }

function Field({ labelAr, labelEn, value, onChange, required, ltr }) {
  const { lang } = useI18n();
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-[#525252] mb-1">{lang === "ar" ? labelAr : labelEn}</div>
      <input type="text" value={value || ""} required={required} dir={ltr ? "ltr" : undefined}
             onChange={(e) => onChange(e.target.value)}
             className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white" />
    </label>
  );
}

function TextArea({ labelAr, labelEn, value, onChange }) {
  const { lang } = useI18n();
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-[#525252] mb-1">{lang === "ar" ? labelAr : labelEn}</div>
      <textarea value={value || ""} rows={3} onChange={(e) => onChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white" />
    </label>
  );
}

// iter295 — URL field with a native "Upload from device" button next to
// it. The hidden <input type="file" accept="image/*" /> is what triggers
// the OS-native picker (Photos / Files) — no in-app gallery, no
// third-party picker. Manual URL typing keeps working unchanged.
function UploadField({ labelAr, labelEn, btnAr, btnEn, value, onChange, onPick, busy, testid }) {
  const { lang } = useI18n();
  const inputRef = useRef(null);
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-[#525252] mb-1">{lang === "ar" ? labelAr : labelEn}</div>
      <div className="flex gap-2 flex-wrap sm:flex-nowrap">
        <input type="text" value={value || ""} dir="ltr"
               onChange={(e) => onChange(e.target.value)}
               className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-[#0a0a0a]/15 text-sm bg-white" />
        <button type="button" disabled={busy}
                onClick={() => inputRef.current?.click()}
                className="shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-bold border-2 border-[#0a0a0a]/10 hover:border-[#c9a55c] bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid={testid}>
          <Upload className="w-3.5 h-3.5" />
          {busy ? (lang === "ar" ? "جارٍ الرفع…" : "Uploading…") : (lang === "ar" ? btnAr : btnEn)}
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
               onChange={(e) => { const f = e.target.files?.[0]; onPick(f); e.target.value = ""; }} />
      </div>
    </label>
  );
}
